import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Spinner } from 'react-bootstrap';
import { MdClose } from 'react-icons/md';
import Card from 'components/inputs/Card';
import Button from 'components/inputs/Button';
import Select, { styles, DropdownIndicator } from 'components/inputs/Select';
import RadioButton from 'components/inputs/RadioButton';
import { CREATE_STATUS_CODE } from 'components/common/constant';
import { API } from 'api';
import { CHECK_FLEET_RESERVATION_AVAILABILITY, CREATE_FLEET_RESERVATION } from 'actions/fleetReservation';
import {
  BUFFER_MINUTES,
  TARGET_TYPES,
  REPEAT_TYPES,
  DAY_CHIPS,
  isRecurring,
  scheduleErrors,
  scheduleSummary,
  formatDate,
  emptySelection,
  connectorLabel,
  hasSelection,
  pruneUnavailable,
  describeResources,
  buildFleetOptions,
  normalizeAvailability,
  asArray,
  unwrap,
  loadStations,
  toStationOptions,
  buildAvailabilityPayload,
  buildCreatePayload,
} from './fleetReservationUtils';

const initialSchedule = { repeatType: 'ONCE', startDate: '', endDate: '', daysOfWeek: [], timeFrom: '', timeTo: '' };

const ReviewItem = ({ label, children, className = '' }) => (
  <div className="fr-review__item">
    <div className="fr-label">{label}</div>
    <div className={className}>{children || '—'}</div>
  </div>
);

ReviewItem.propTypes = { label: PropTypes.string, children: PropTypes.node, className: PropTypes.string };

const ResourceTile = ({ title, subtitle, available, conflict, selected, onSelect }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={!available}
    className={`fr-tile ${selected ? 'is-selected' : ''} ${available ? '' : 'is-busy'}`}
    onClick={onSelect}
  >
    <span className="d-flex justify-content-between">
      <b>{title}</b>
      <span className="fr-tile__check">{selected ? '✓' : ''}</span>
    </span>
    <span className="fr-muted small">{subtitle}</span>
    <span className="fr-tile__state">{available ? (selected ? 'Selected' : 'Available') : conflict || 'Reserved'}</span>
  </button>
);

ResourceTile.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  available: PropTypes.bool,
  conflict: PropTypes.string,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
};

const FleetReservationCreate = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isAvailabilityLoading, isSaving } = useSelector((state) => state.fleetReservation);

  const [stations, setStations] = useState([]);
  const [isStationsLoading, setIsStationsLoading] = useState(true);
  const [stationId, setStationId] = useState('');
  const [fleet, setFleet] = useState(null);
  const [schedule, setSchedule] = useState(initialSchedule);
  const [tree, setTree] = useState([]);
  const [selection, setSelection] = useState(emptySelection());
  const [reason, setReason] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [backendOccurrenceCount, setBackendOccurrenceCount] = useState(null);

  const stationOptions = useMemo(() => toStationOptions(stations), [stations]);
  const station = useMemo(() => _.find(stations, (item) => (item.id || item._id) === stationId), [stations, stationId]);
  const errors = scheduleErrors(schedule);
  const visibleErrors = showErrors ? errors : {};
  const scheduleReady = _.isEmpty(errors);
  const summary = useMemo(() => scheduleSummary(schedule), [schedule]);
  const dateCount = backendOccurrenceCount === null ? summary.occurrences : backendOccurrenceCount;
  const resources = stationId && availabilityChecked ? describeResources(selection, tree) : [];
  const isWeekly = schedule.repeatType === 'WEEKLY';
  const isCheckingAvailability = !!stationId && scheduleReady && (!availabilityChecked || isAvailabilityLoading);
  const canSubmit = !!fleet && !!stationId && scheduleReady && availabilityChecked && !isAvailabilityLoading && hasSelection(selection, tree);

  useEffect(() => {
    loadStations()
      .then((list) => setStations(list.filter((item) => item.access_type === 'Public' && !item.is_external_station)))
      .catch(() => setStations([]))
      .finally(() => setIsStationsLoading(false));
  }, []);

  useEffect(() => {
    setTree([]);
    setSelection((prev) => emptySelection(prev.mode));
  }, [stationId]);

  const loadFleetOptions = useMemo(
    () =>
      _.debounce((search, callback) => {
        API.getFleetReservationFleetOptions({ search, limit: 20 })
          .then((res) => callback(buildFleetOptions(asArray(unwrap(_.get(res, 'data'))))))
          .catch(() => callback([]));
      }, 300),
    []
  );

  const checkAvailability = useMemo(
    () =>
      _.debounce((id, sched) => {
        dispatch({
          type: CHECK_FLEET_RESERVATION_AVAILABILITY.REQUEST,
          payload: buildAvailabilityPayload(id, sched),
          cb: (res) => {
            setAvailabilityChecked(true);
            if (_.get(res, 'status') !== 200) {
              setTree([]);
              setAvailabilityError(_.get(res, 'data.message') || 'Could not check availability. Adjust the schedule and try again.');
              return;
            }
            const data = unwrap(_.get(res, 'data'));
            const nextTree = normalizeAvailability(data);
            setAvailabilityError('');
            setBackendOccurrenceCount(_.isNumber(_.get(data, 'occurrenceCount')) ? data.occurrenceCount : null);
            setTree(nextTree);
            setSelection((prev) => pruneUnavailable(prev, nextTree));
          },
        });
      }, 250),
    []
  );

  useEffect(
    () => () => {
      checkAvailability.cancel();
      loadFleetOptions.cancel();
    },
    []
  );

  useEffect(() => {
    setConflicts([]);
    setAvailabilityChecked(false);
    setAvailabilityError('');
    setBackendOccurrenceCount(null);
    if (stationId && scheduleReady) checkAvailability(stationId, schedule);
  }, [stationId, schedule]);

  const setScheduleField = (key, value) => setSchedule((prev) => ({ ...prev, [key]: value }));
  const setRepeatType = (repeatType) =>
    setSchedule((prev) => ({
      ...prev,
      repeatType,
      endDate: repeatType === 'ONCE' ? '' : prev.endDate,
      daysOfWeek: repeatType === 'WEEKLY' ? prev.daysOfWeek : [],
    }));
  const toggleDay = (day) =>
    setScheduleField('daysOfWeek', schedule.daysOfWeek.includes(day) ? schedule.daysOfWeek.filter((d) => d !== day) : [...schedule.daysOfWeek, day]);

  const submit = (addAnother) => {
    setShowErrors(true);
    if (!canSubmit) return;
    dispatch({
      type: CREATE_FLEET_RESERVATION.REQUEST,
      payload: buildCreatePayload({ stationId, schedule, fleet, selection, reason }),
      cb: (res) => {
        if (_.get(res, 'status') === CREATE_STATUS_CODE) {
          const code = _.get(unwrap(_.get(res, 'data')), 'reservationCode', '');
          toast.success(_.get(res, 'data.message') || `Reservation ${code} created`);
          if (addAnother) {
            setStationId('');
            setReason('');
            setShowErrors(false);
          } else {
            onCreated(code);
          }
          return;
        }
        setConflicts(_.get(res, 'data.conflicts', []));
      },
    });
  };

  const submitHint = () => {
    if (!stationId) return 'Select a station';
    if (!fleet) return 'Select a fleet';
    if (!scheduleReady) return 'Complete the schedule';
    if (isCheckingAvailability) return 'Checking availability…';
    if (availabilityError) return 'Availability check failed';
    if (!hasSelection(selection, tree)) return selection.mode === 'STATION' ? 'No charger is free for this schedule' : 'Select an available resource';
    return '';
  };

  const renderAvailability = () => {
    if (!stationId) return <div className="fr-empty-note">Select a station above to see its chargers.</div>;
    if (!scheduleReady) return <div className="fr-empty-note">Complete the schedule above to see which chargers are free.</div>;
    if (isCheckingAvailability) {
      return (
        <div className="fr-empty-note">
          <Spinner as="span" animation="border" size="sm" className="me-2" />
          Checking availability…
        </div>
      );
    }
    if (availabilityError) return <div className="fr-empty-note fr-empty-note--error">{availabilityError}</div>;
    if (tree.length === 0) return <div className="fr-empty-note">No chargers found at this station.</div>;

    if (selection.mode === 'STATION') {
      return (
        <div className="fr-box">
          {tree.map((charger) => (
            <div key={charger.chargerId} className="fr-row">
              <div>
                <b>{charger.ocppId}</b>
                <span className="fr-muted small ms-2">
                  {charger.connectors.length} connector{charger.connectors.length === 1 ? '' : 's'}
                </span>
              </div>
              {charger.isAvailable ? (
                <span className="fr-tag fr-tag--ok">Included</span>
              ) : (
                <span className="fr-tag fr-tag--busy" title={charger.conflictLabel}>
                  Skipped · reserved
                </span>
              )}
            </div>
          ))}
          <div className="fr-muted small mt-2">Reserved chargers are skipped. A charger that frees up later is not added automatically.</div>
        </div>
      );
    }

    if (selection.mode === 'CHARGER') {
      return (
        <div className="fr-tile-grid" role="radiogroup">
          {tree.map((charger) => (
            <ResourceTile
              key={charger.chargerId}
              title={charger.ocppId}
              subtitle={charger.connectors.map((connector) => connectorLabel(connector.connectorId, connector.name)).join(', ')}
              available={charger.isAvailable}
              conflict={charger.conflictLabel}
              selected={selection.chargerId === charger.chargerId}
              onSelect={() => setSelection({ mode: 'CHARGER', chargerId: charger.chargerId, connectorId: null })}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-3" role="radiogroup">
        {tree.map((charger) => (
          <div key={charger.chargerId} className="fr-box">
            <div className="d-flex justify-content-between mb-2">
              <b>{charger.ocppId}</b>
              <span className="fr-muted small">
                {charger.connectors.length} connector{charger.connectors.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="fr-tile-grid">
              {charger.connectors.map((connector) => (
                <ResourceTile
                  key={connector.connectorId}
                  title={connectorLabel(connector.connectorId, connector.name)}
                  subtitle={[connector.type, connector.power].filter(Boolean).join(' · ') || '—'}
                  available={connector.isAvailable}
                  conflict={connector.conflictLabel}
                  selected={selection.chargerId === charger.chargerId && selection.connectorId === connector.connectorId}
                  onSelect={() => setSelection({ mode: 'CONNECTOR', chargerId: charger.chargerId, connectorId: connector.connectorId })}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const hint = submitHint();

  return (
    <Modal show centered size="xl" scrollable onHide={onClose} dialogClassName="fr-create-modal">
      <div className="fr-page fr-create-modal__body">
        <div className="fr-modal-header">
          <h5 className="mb-0">{t('header.createFleetReservation', 'Create Reservation')}</h5>
          <MdClose size={28} color="#BE210B" className="fr-modal-header__close" onClick={onClose} />
        </div>
        <div className="row g-3">
          <div className="col-12 col-lg-8">
            <Card>
              <div className="fr-section-title">
                <span className="fr-step">1</span> Station and fleet
              </div>
              <div className="fr-two-col">
                <Select
                  label="Charging station — one per reservation"
                  placeholder={isStationsLoading ? 'Loading stations…' : 'Select station'}
                  isLoading={isStationsLoading}
                  options={stationOptions}
                  disableSort
                  value={stationId}
                  onChange={(value) => setStationId(value || '')}
                  error={showErrors && !stationId ? 'Select a station' : ''}
                />
                <div className="select-input-form-group">
                  <label className="form-select-label" htmlFor="fr-fleet">
                    Fleet
                  </label>
                  <AsyncSelect
                    inputId="fr-fleet"
                    className="form-field react-select"
                    classNamePrefix="react-select"
                    styles={styles()}
                    components={{ DropdownIndicator }}
                    cacheOptions
                    defaultOptions
                    loadOptions={loadFleetOptions}
                    value={fleet}
                    onChange={setFleet}
                    placeholder="Search fleet owner or driver"
                    noOptionsMessage={() => 'No fleet found'}
                  />
                  {showErrors && !fleet && <div className="text-danger small mt-1">Select a fleet</div>}
                </div>
              </div>
            </Card>

            <Card>
              <div className="fr-section-title">
                <span className="fr-step">2</span> Schedule
                <span className="fr-section-hint">Set the time first, so availability shows on each charger below</span>
              </div>

              <div className="fr-label">Repeat type</div>
              <div className="fr-radio-row">
                {REPEAT_TYPES.map((type) => (
                  <RadioButton
                    key={type.value}
                    id={`fr-repeat-${type.value}`}
                    name="repeatType"
                    checked={schedule.repeatType === type.value}
                    onChange={() => setRepeatType(type.value)}
                  >
                    {type.label}
                  </RadioButton>
                ))}
              </div>

              <div className="fr-schedule-grid">
                <div>
                  <div className="fr-label">Start date *</div>
                  <input
                    type="date"
                    className="form-control fr-input"
                    value={schedule.startDate}
                    min={moment().format('YYYY-MM-DD')}
                    onChange={(e) => setScheduleField('startDate', e.target.value)}
                  />
                  {visibleErrors.startDate && <div className="text-danger small">{visibleErrors.startDate}</div>}
                </div>
                {isRecurring(schedule.repeatType) && (
                  <div>
                    <div className="fr-label">End date *</div>
                    <input
                      type="date"
                      className="form-control fr-input"
                      value={schedule.endDate}
                      min={schedule.startDate || moment().format('YYYY-MM-DD')}
                      onChange={(e) => setScheduleField('endDate', e.target.value)}
                    />
                    {visibleErrors.endDate && <div className="text-danger small">{visibleErrors.endDate}</div>}
                  </div>
                )}
                <div>
                  <div className="fr-label">Time from *</div>
                  <input
                    type="time"
                    className="form-control fr-input"
                    value={schedule.timeFrom}
                    onChange={(e) => setScheduleField('timeFrom', e.target.value)}
                  />
                  {visibleErrors.timeFrom && <div className="text-danger small">{visibleErrors.timeFrom}</div>}
                </div>
                <div>
                  <div className="fr-label">Time to *</div>
                  <input
                    type="time"
                    className="form-control fr-input"
                    value={schedule.timeTo}
                    onChange={(e) => setScheduleField('timeTo', e.target.value)}
                  />
                  {visibleErrors.timeTo && <div className="text-danger small">{visibleErrors.timeTo}</div>}
                </div>
              </div>
              {summary.blocked && (
                <div className="fr-helper">
                  Effective blocked window with {BUFFER_MINUTES}m buffer: <b className="fr-blocked">{summary.blocked}</b>
                </div>
              )}

              <div className={`mt-3 ${isWeekly ? '' : 'opacity-50'}`}>
                <div className="fr-label">Repeat on {!isWeekly && <span className="text-lowercase">(weekly only)</span>}</div>
                <div className="fr-day-chips">
                  {DAY_CHIPS.map((chip) => (
                    <button
                      type="button"
                      key={chip.value}
                      title={chip.name}
                      disabled={!isWeekly}
                      className={isWeekly && schedule.daysOfWeek.includes(chip.value) ? 'active' : ''}
                      onClick={() => toggleDay(chip.value)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
                {visibleErrors.daysOfWeek && <div className="text-danger small">{visibleErrors.daysOfWeek}</div>}
              </div>

              {dateCount > 0 && (
                <div className="fr-summary-bar">
                  <span>{summary.repeat}</span>
                  <span>
                    <b>{dateCount}</b> occurrence{dateCount === 1 ? '' : 's'}
                  </span>
                </div>
              )}
            </Card>

            <Card>
              <div className="fr-section-title">
                <span className="fr-step">3</span> Resources
              </div>
              <div className="fr-label">Target type</div>
              <div className="fr-radio-row">
                {TARGET_TYPES.map((type) => (
                  <RadioButton
                    key={type.value}
                    id={`fr-target-${type.value}`}
                    name="targetType"
                    checked={selection.mode === type.value}
                    onChange={() => setSelection(emptySelection(type.value))}
                  >
                    {type.label}
                  </RadioButton>
                ))}
              </div>

              {renderAvailability()}

              <div className="mt-3">
                <div className="fr-label">Reason / remarks (optional)</div>
                <input className="form-control fr-input" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            </Card>
          </div>

          <div className="col-12 col-lg-4">
            <div className="fr-review-col">
              <div className="fr-review">
                <h5>Review</h5>
                <ReviewItem label="Station">{_.get(station, 'name', '')}</ReviewItem>
                <ReviewItem label="Fleet">{_.get(fleet, 'fleetName', '')}</ReviewItem>
                <ReviewItem label="Resource">{resources.length > 0 && resources.map((resource) => <div key={resource}>{resource}</div>)}</ReviewItem>
                <ReviewItem label="Booked window">{summary.booked}</ReviewItem>
                <ReviewItem label="Public blocked" className="fr-blocked">
                  {summary.blocked}
                </ReviewItem>
                <ReviewItem label="Repeat">{dateCount > 0 && summary.repeat}</ReviewItem>
                <ReviewItem label="Occurrences">{dateCount > 0 && `${dateCount} date${dateCount === 1 ? '' : 's'}`}</ReviewItem>
                {conflicts.length > 0 ? (
                  <div className="fr-review__bad">
                    {conflicts.map((conflict) => (
                      <div key={`${conflict.reservationCode}-${conflict.reservationDate}`}>
                        {formatDate(conflict.reservationDate)} conflicts with {conflict.reservationCode}
                      </div>
                    ))}
                  </div>
                ) : (
                  canSubmit && <div className="fr-review__ok">✓ Available for the selected schedule</div>
                )}
              </div>
              <div className="fr-review__note">
                A reservation covers one station. To book another station, use <b>Save and Add Another</b> — fleet and schedule are kept.
              </div>
              <div className="fr-review__actions">
                {hint && <div className="fr-muted small text-center">{hint}</div>}
                <Button disabled={!canSubmit || isSaving} onClick={() => submit(false)}>
                  {(isCheckingAvailability || isSaving) && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                  Create Reservation
                </Button>
                <button type="button" className="btn fr-btn-danger" disabled={!canSubmit || isSaving} onClick={() => submit(true)}>
                  Save and Add Another
                </button>
                <button type="button" className="btn btn-link fr-muted" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

FleetReservationCreate.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default FleetReservationCreate;
